import { inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { TokenService } from '../services/token.service';

type Role = 'admin' | 'client' | 'provider';

const DEFAULT_ROUTE: Record<Role, string> = {
  admin: '/admin/dashboard',
  client: '/client/dashboard',
  provider: '/provider/dashboard',
};

/**
 * Functional guard factory. Pass the roles allowed to access the route.
 * Unauthorized users are redirected to their own default dashboard.
 *
 * @example
 * canActivate: [roleGuard(['admin'])]
 * canActivate: [roleGuard(['admin', 'client'])]
 */
export const roleGuard = (allowedRoles: Role[]) => (): boolean | UrlTree => {
  const tokenService = inject(TokenService);
  const router = inject(Router);
  const role = tokenService.role();
  if (allowedRoles.includes(role)) return true;
  return router.createUrlTree([DEFAULT_ROUTE[role]]);
};

/**
 * Guard for the login page — redirects already-authenticated users to their
 * dashboard so they don't see the login form again.
 */
export const loggedInGuard = (): boolean | UrlTree => {
  const tokenService = inject(TokenService);
  const router = inject(Router);
  if (!tokenService.jwtToken()) return true;
  return router.createUrlTree([DEFAULT_ROUTE[tokenService.role()]]);
};
